import React, { useState } from "react";
import {
  Modal, Form, InputNumber, Select, DatePicker, Input, Upload,
  message, Alert,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { API_DOMAIN } from "../constants";

dayjs.extend(utc);

const METHOD_OPTIONS = [
  { value: "cash",          label: "Cash" },
  { value: "card",          label: "Card" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "pay_link",      label: "Pay By Link" },
  { value: "cod",           label: "Cash on Delivery" },
  { value: "cheque",        label: "Cheque" },
];

const STAGE_OPTIONS = [
  { value: "advance",       label: "Advance" },
  { value: "balance",       label: "Balance" },
  { value: "full_payment",  label: "Full Payment" },
  { value: "refund",        label: "Refund" },
  { value: "adjustment",    label: "Adjustment" },
];

const SOURCE_OPTIONS = [
  { value: "office",    label: "Office" },
  { value: "bank",      label: "Bank" },
  { value: "online",    label: "Online" },
  { value: "delivery",  label: "Delivery" },
  { value: "whatsapp",  label: "WhatsApp" },
];

const ONLINE_METHODS = new Set(["bank_transfer", "pay_link"]);

// cash/card = immediately received; others start as pending
function deriveStatus(method) {
  if (!method) return null;
  return ["cash", "card"].includes(method) ? "received" : "pending";
}

export function RecordPaymentModal({ open, queryPk, userId, afterSales, onClose, onSuccess }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [parkTask, setParkTask] = useState(false);
  const [method, setMethod] = useState(null);
  const headers = { accessToken: localStorage.getItem("accessToken") };

  const isOnline = ONLINE_METHODS.has(method);
  const derivedStatus = deriveStatus(method);

  const handleClose = () => {
    form.resetFields();
    setFileList([]);
    setParkTask(false);
    setMethod(null);
    onClose();
  };

  const saleAmount = Number(afterSales?.sale_amount || 0);

  const handleAmountChange = (val) => {
    if (!saleAmount || !val) return;
    const currentStage = form.getFieldValue("payment_stage");
    if (val < saleAmount) {
      if (currentStage === "full_payment") {
        form.setFieldValue("payment_stage", "advance");
        setParkTask(true);
      }
    } else {
      if (currentStage === "advance") {
        form.setFieldValue("payment_stage", "full_payment");
        setParkTask(false);
      }
    }
  };

  const handleStageChange = (val) => {
    if (val === "advance") {
      setParkTask(true);
    } else if (val === "full_payment" || val === "balance") {
      setParkTask(false);
    }
  };

  const handleMethodChange = (val) => {
    setMethod(val);
    if (ONLINE_METHODS.has(val)) {
      form.setFieldsValue({
        payment_source: "online",
        collected_from: undefined,
        collection_location: undefined,
      });
    }
    if (["cash", "card"].includes(val)) {
      form.setFieldValue("paid_at", dayjs());
    }
  };

  const handleOk = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const proofUrls = await Promise.all(
        fileList.map(async f => {
          const fd = new FormData();
          fd.append("file", f.originFileObj);
          const res = await axios.post(`${API_DOMAIN}/whatsapp/image_upload`, fd, { headers });
          return res.data;
        })
      );

      const status = derivedStatus || "pending";
      const res = await axios.post(
        `${API_DOMAIN}/queries/${queryPk}/payments`,
        {
          userId,
          amount: values.amount,
          payment_method: values.payment_method,
          payment_stage: values.payment_stage,
          payment_status: status,
          paid_at: values.paid_at
            ? values.paid_at.utc().format("YYYY-MM-DD HH:mm:ss")
            : null,
          payment_reference: values.payment_reference?.trim() || null,
          payment_source: values.payment_source || null,
          collected_from: isOnline ? null : (values.collected_from?.trim() || null),
          collection_location: isOnline ? null : (values.collection_location?.trim() || null),
          received_by: status === "received" ? userId : null,
          remarks: values.remarks?.trim() || null,
          proof_images: proofUrls,
          ...(parkTask && values.park_until ? {
            park_until: values.park_until.utc().format("YYYY-MM-DD HH:mm:ss"),
            park_reason: values.park_reason?.trim() || null,
          } : {}),
        },
        { headers }
      );
      if (!res.data?.success) throw new Error(res.data?.message);
      message.success("Payment recorded");
      onSuccess();
      handleClose();
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || "Failed to record payment");
    } finally { setSaving(false); }
  };

  const getDefaultValues = () => {
    const paidAmount    = Number(afterSales?.paid_amount   || 0);
    const balanceAmount = Number(afterSales?.balance_amount || 0);
    const saleAmount    = Number(afterSales?.sale_amount   || 0);
    const hasPartial    = paidAmount > 0 && balanceAmount > 0;
    return {
      amount: hasPartial ? balanceAmount : (saleAmount || undefined),
      payment_stage: hasPartial ? "balance" : "full_payment",
      paid_at: dayjs(),
    };
  };

  return (
    <Modal
      open={open}
      title="Record Payment"
      onCancel={handleClose}
      onOk={handleOk}
      confirmLoading={saving}
      okText="Record"
      destroyOnClose
      styles={{ body: { overflowY: "auto", maxHeight: "calc(90vh - 160px)", paddingRight: 4 } }}
      afterOpenChange={isOpen => {
        if (isOpen) form.setFieldsValue(getDefaultValues());
      }}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>

        <div style={{ display: "flex", gap: 12 }}>
          <Form.Item name="amount" label="Amount (AED)" rules={[{ required: true, message: "Required" }]} style={{ flex: 1 }}>
            <InputNumber
              min={0} precision={2}
              style={{ width: "100%" }}
              placeholder="0.00"
              prefix="AED"
              onChange={handleAmountChange}
            />
          </Form.Item>
          <Form.Item name="payment_stage" label="Stage" rules={[{ required: true, message: "Required" }]} style={{ flex: 1 }}>
            <Select placeholder="Select..." options={STAGE_OPTIONS} onChange={handleStageChange} />
          </Form.Item>
        </div>

        <Form.Item name="payment_method" label="Payment Method" rules={[{ required: true, message: "Required" }]}>
          <Select
            placeholder="Select method..."
            options={METHOD_OPTIONS}
            onChange={handleMethodChange}
          />
        </Form.Item>

        {derivedStatus && (
          <Alert
            type={derivedStatus === "received" ? "success" : "info"}
            message={
              derivedStatus === "received"
                ? "This payment will be recorded as Received immediately."
                : "This payment will be recorded as Pending — confirm later via Mark as Received."
            }
            showIcon
            style={{ marginBottom: 16, padding: "4px 12px", fontSize: 12 }}
          />
        )}

        <Form.Item
          name="paid_at"
          label={derivedStatus === "pending" ? "Expected / Transaction Date" : "Payment Date & Time"}
          rules={derivedStatus === "received" ? [{ required: true, message: "Required" }] : []}
        >
          <DatePicker showTime style={{ width: "100%" }} format="DD MMM YYYY HH:mm" />
        </Form.Item>

        <Form.Item name="payment_reference" label="Payment Reference">
          <Input placeholder="e.g. TRN-20260628-001, cheque number, transfer screenshot ref" />
        </Form.Item>

        <Form.Item name="payment_source" label="Source">
          <Select
            placeholder="Where?"
            options={SOURCE_OPTIONS}
            allowClear
            disabled={isOnline}
          />
        </Form.Item>

        {!isOnline && (
          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="collected_from" label="Collected From" style={{ flex: 1 }}>
              <Input placeholder="e.g. Customer, Insurance, Corporate" />
            </Form.Item>
            <Form.Item name="collection_location" label="Collection Location" style={{ flex: 1 }}>
              <Input placeholder="e.g. Office, Workshop" />
            </Form.Item>
          </div>
        )}

        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} placeholder="Any notes about this payment" />
        </Form.Item>

        <Form.Item label="Proof Images (optional)">
          <Upload
            listType="picture-card"
            fileList={fileList}
            beforeUpload={() => false}
            onChange={({ fileList: fl }) => setFileList(fl)}
            accept="image/*"
            multiple
          >
            {fileList.length < 5 && (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 4, fontSize: 11 }}>Add</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item label="Park task for follow-up?">
          <input
            type="checkbox"
            checked={parkTask}
            onChange={e => setParkTask(e.target.checked)}
            style={{ marginRight: 8 }}
          />
          <span style={{ fontSize: 12, color: "#595959" }}>Partial payment — customer will return</span>
        </Form.Item>
        {parkTask && (
          <>
            <Form.Item name="park_until" label="Follow-up Date & Time" rules={[{ required: true, message: "Required" }]}>
              <DatePicker showTime style={{ width: "100%" }} format="DD MMM YYYY HH:mm" />
            </Form.Item>
            <Form.Item name="park_reason" label="Park Reason">
              <Input.TextArea rows={2} placeholder="e.g. Customer will pay balance tomorrow" />
            </Form.Item>
          </>
        )}

      </Form>
    </Modal>
  );
}
